// Read-only template preview modal. Renders the template subject + body
// against placeholder values returned by GET /message-templates/:uuid/preview
// so the staff member can see what customers will receive without sending.

import { useQuery } from '@tanstack/react-query';
import { Modal } from '../../../../components/ui/Modal/Modal';
import { Button } from '../../../../components/ui/Button/Button';
import { Spinner } from '../../../../components/ui/Spinner/Spinner';
import { previewTemplate } from '../../../../api/templates';
import type { RenderedTemplate, TemplateInfo } from '../../../../types/api';
import styles from './TemplatePreviewModal.module.css';

export interface TemplatePreviewModalProps {
  open: boolean;
  onClose: () => void;
  template: Pick<TemplateInfo, 'uuid' | 'template_name' | 'channel_type'> | null;
}

export function TemplatePreviewModal({
  open,
  onClose,
  template,
}: TemplatePreviewModalProps): JSX.Element | null {
  const query = useQuery<RenderedTemplate>({
    queryKey: ['templates', 'preview', template?.uuid ?? 'none'],
    queryFn: () => previewTemplate(template!.uuid),
    enabled: open && template !== null,
    staleTime: 30_000,
  });

  if (!open || template === null) return null;

  const isEmail = template.channel_type === 'email';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Preview — ${template.template_name}`}
      size="lg"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className={styles.body}>
        <p className={styles.note}>
          Showing the rendered output with placeholder values
          (e.g. <code>{'{{customer_name}}'}</code> → <code>John Smith</code>).
          No message is sent.
        </p>

        {query.isLoading ? (
          <div className={styles.loading} role="status" aria-label="Loading preview">
            <Spinner />
          </div>
        ) : query.isError ? (
          <p role="alert" className={styles.error}>
            Could not load preview. Please try again.
          </p>
        ) : query.data ? (
          <div className={styles.sections}>
            {isEmail && query.data.subject !== null ? (
              <section>
                <h4 className={styles.heading}>Subject</h4>
                <div className={styles.subject}>{query.data.subject}</div>
              </section>
            ) : null}

            {isEmail && query.data.body_html ? (
              <section>
                <h4 className={styles.heading}>HTML body</h4>
                <iframe
                  title="HTML body preview"
                  className={styles.htmlFrame}
                  // Sandbox without `allow-scripts` — the rendered HTML
                  // never executes, so we can render tenant-authored
                  // markup safely without a third-party sanitiser.
                  sandbox=""
                  srcDoc={query.data.body_html}
                />
              </section>
            ) : null}

            {query.data.body_text ? (
              <section>
                <h4 className={styles.heading}>
                  {isEmail ? 'Plain-text body' : 'Body'}
                </h4>
                <pre className={styles.textBody}>{query.data.body_text}</pre>
              </section>
            ) : null}

            {!query.data.body_html && !query.data.body_text ? (
              <p className={styles.muted}>
                This template has no body content.
              </p>
            ) : null}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
